'use client'

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { useSSEEvent } from "@/components/providers/sse-provider"
import {
  getDeployStatus,
  buildStage,
  deployStage,
  deployProd,
  approveDeployAction,
  rollbackDeployAction,
  type DeployState,
  type DeployRecord,
} from "@/app/actions/vds-update"
import {
  RefreshCw,
  Rocket,
  CheckCircle,
  XCircle,
  Clock,
  GitCommit,
  Package,
  Loader2,
  ChevronDown,
  RotateCcw,
  AlertTriangle,
  Hammer,
} from "lucide-react"

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString()
}

function formatTimeRemaining(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'success':
      return <Badge className="bg-green-500 text-white border-0">Success</Badge>
    case 'failed':
      return <Badge className="bg-red-500 text-white border-0">Failed</Badge>
    case 'pending':
      return <Badge className="bg-yellow-500 text-white border-0">Pending</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

function getActionIcon(action: string) {
  switch (action) {
    case 'deploy':
      return <Rocket className="h-4 w-4" />
    case 'rollback':
      return <RotateCcw className="h-4 w-4" />
    case 'approve':
      return <CheckCircle className="h-4 w-4" />
    default:
      return <Package className="h-4 w-4" />
  }
}

export function VDSUpdatePanel() {
  const [state, setState] = useState<DeployState | null>(null)
  const [loading, setLoading] = useState(true)
  const [building, setBuilding] = useState(false)
  const [deployingStage, setDeployingStage] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [approving, setApproving] = useState(false)
  const [rollingBack, setRollingBack] = useState(false)
  const [branch, setBranch] = useState('main')
  const [buildLogs, setBuildLogs] = useState('')
  const [showBuildLogs, setShowBuildLogs] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [confirmRollbackOpen, setConfirmRollbackOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getDeployStatus()
      setState(data)
    } catch (error) {
      console.error('Failed to fetch deploy status:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  // Listen for SSE events - refresh state on any VDS event
  useSSEEvent(
    ['vds_stage_build', 'vds_deploy_pending', 'vds_deploy_approved', 'vds_deploy_rollback', 'vds_deploy_failed'],
    () => fetchStatus(),
    [fetchStatus]
  )

  // Countdown timer for pending approval
  useEffect(() => {
    if (!state?.pending) {
      setTimeRemaining(0)
      return
    }

    const updateTimer = () => {
      const now = new Date()
      const expiresAt = new Date(state.pending!.expiresAt)
      const remaining = Math.max(0, Math.round((expiresAt.getTime() - now.getTime()) / 1000))
      setTimeRemaining(remaining)

      if (remaining === 0) {
        // Auto-refresh when timer expires
        fetchStatus()
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [state?.pending, fetchStatus])

  const handleBuildStage = async () => {
    setBuilding(true)
    setBuildLogs('')
    setShowBuildLogs(true)
    try {
      const result = await buildStage(branch)
      setBuildLogs(result.logs)
      if (result.success) {
        await fetchStatus()
      }
    } catch (error) {
      setBuildLogs(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setBuilding(false)
    }
  }

  const handleDeployStage = async () => {
    setDeployingStage(true)
    setBuildLogs('')
    setShowBuildLogs(true)
    try {
      const result = await deployStage()
      setBuildLogs(result.logs)
      if (result.success && result.url) {
        setBuildLogs(prev => prev + `\n🌍 Stage URL: ${result.url}`)
      }
      await fetchStatus()
    } catch (error) {
      console.error('Stage deploy failed:', error)
    } finally {
      setDeployingStage(false)
    }
  }

  const handleDeployProd = async () => {
    setDeploying(true)
    try {
      const result = await deployProd()
      if (!result.success) {
        setBuildLogs(result.logs)
        setShowBuildLogs(true)
      }
      await fetchStatus()
    } catch (error) {
      console.error('Deploy failed:', error)
    } finally {
      setDeploying(false)
    }
  }

  const handleApprove = async () => {
    setApproving(true)
    try {
      await approveDeployAction()
      await fetchStatus()
    } catch (error) {
      console.error('Approve failed:', error)
    } finally {
      setApproving(false)
    }
  }

  const handleRollback = async () => {
    setRollingBack(true)
    setConfirmRollbackOpen(false)
    try {
      const result = await rollbackDeployAction('Manual rollback from dashboard')
      if (!result.success) {
        setBuildLogs(result.logs)
        setShowBuildLogs(true)
      }
      await fetchStatus()
    } catch (error) {
      console.error('Rollback failed:', error)
    } finally {
      setRollingBack(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Pending Deploy Alert */}
      {state?.pending && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">
                    New Version Deployed - Awaiting Approval
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    <GitCommit className="inline h-3 w-3 mr-1" />
                    <span className="font-mono">{state.pending.version.commitHash}</span>
                    {' - '}
                    {state.pending.version.commitMessage}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <Clock className="h-4 w-4" />
                  <span>Auto-rollback in: </span>
                  <span className="font-mono font-bold text-lg">
                    {formatTimeRemaining(timeRemaining)}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleApprove}
                    disabled={approving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {approving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="mr-2 h-4 w-4" />
                    )}
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmRollbackOpen(true)}
                    disabled={rollingBack}
                  >
                    {rollingBack ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Rollback
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Version */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-500/20 rounded">
              <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            VDS Manager Version
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 ml-auto"
              onClick={fetchStatus}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state?.current ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <GitCommit className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm">{state.current.commitHash}</span>
                <Badge variant="outline">{state.current.branch}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{state.current.commitMessage}</p>
              <p className="text-xs text-muted-foreground">
                Deployed: {formatDate(state.current.timestamp)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No version info available</p>
          )}
        </CardContent>
      </Card>

      {/* Stage Build */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-500/20 rounded">
              <Hammer className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            Stage Build
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="branch" className="text-xs">Branch</Label>
              <Input
                id="branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="main"
                className="h-9"
              />
            </div>
            <Button onClick={handleBuildStage} disabled={building}>
              {building ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Hammer className="mr-2 h-4 w-4" />
              )}
              Build Stage
            </Button>
          </div>

          {state?.stageBuild && (
            <div className="p-3 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitCommit className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{state.stageBuild.version.commitHash}</span>
                </div>
                {state.stageBuild.status === 'ready' ? (
                  <Badge className="bg-green-500 text-white border-0">Ready</Badge>
                ) : state.stageBuild.status === 'building' ? (
                  <Badge className="bg-yellow-500 text-white border-0">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Building
                  </Badge>
                ) : (
                  <Badge className="bg-red-500 text-white border-0">Failed</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{state.stageBuild.version.commitMessage}</p>
              <p className="text-xs text-muted-foreground">
                Built: {formatDate(state.stageBuild.builtAt)}
              </p>

              {state.stageBuild.status === 'ready' && (
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="secondary"
                    onClick={handleDeployStage}
                    disabled={deployingStage || deploying}
                    className="flex-1"
                  >
                    {deployingStage ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Rocket className="mr-2 h-4 w-4" />
                    )}
                    Deploy to Stage
                  </Button>
                  {!state.pending && (
                    <Button
                      onClick={handleDeployProd}
                      disabled={deploying || deployingStage}
                      className="flex-1"
                    >
                      {deploying ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Rocket className="mr-2 h-4 w-4" />
                      )}
                      Deploy to Prod
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Build Logs */}
          {buildLogs && (
            <Collapsible open={showBuildLogs} onOpenChange={setShowBuildLogs}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  Build Logs
                  <ChevronDown className={`h-4 w-4 transition-transform ${showBuildLogs ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ScrollArea className="h-64 mt-2">
                  <pre className="p-3 bg-muted rounded text-xs font-mono whitespace-pre-wrap">
                    {buildLogs}
                  </pre>
                </ScrollArea>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      {/* Deploy History */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CollapsibleTrigger asChild>
              <CardTitle className="text-base flex items-center gap-2 cursor-pointer hover:text-muted-foreground transition-colors">
                <div className="p-1.5 bg-purple-100 dark:bg-purple-500/20 rounded">
                  <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                Deploy History
                <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-4 px-0">
                {state?.history && state.history.length > 0 ? (
                  <div className="space-y-2">
                    {state.history.slice(0, 10).map((record: DeployRecord, index: number) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors overflow-hidden"
                      >
                        <div className="p-1.5 bg-muted rounded shrink-0">
                          {getActionIcon(record.action)}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="capitalize text-sm font-medium">{record.action}</span>
                            {getStatusBadge(record.status)}
                            <span className="text-xs text-muted-foreground sm:ml-auto">
                              {formatDate(record.timestamp)}
                            </span>
                          </div>
                          <div className="flex items-start gap-1.5 text-xs text-muted-foreground mt-0.5">
                            <GitCommit className="h-3 w-3 shrink-0 mt-0.5" />
                            <span>
                              <span className="font-mono">{record.version.commitHash}</span>
                              {' · '}
                              <span className="break-words">{record.version.commitMessage}</span>
                            </span>
                          </div>
                          {record.message && (
                            <p className="text-xs text-muted-foreground mt-0.5 italic truncate">
                              {record.message}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No deploy history yet
                  </p>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </CardHeader>
      </Card>

      {/* Rollback Confirmation Dialog */}
      <AlertDialog open={confirmRollbackOpen} onOpenChange={setConfirmRollbackOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Rollback</AlertDialogTitle>
            <AlertDialogDescription>
              This will rollback to the previous version:
              {state?.previous && (
                <span className="block mt-2 font-mono text-sm">
                  {state.previous.commitHash} - {state.previous.commitMessage}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollback} className="bg-destructive text-white hover:bg-destructive/90">
              Rollback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

